import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as THREE from 'three';
import './ModelViewer.css';
import { getMuscleInfo } from './muscleNames';
import { isIOS } from './deviceDetect';
import { useFocusMeshOnLoad } from './useFocusMeshOnLoad';
import { useMeshSelection } from './useMeshSelection';
import ModelSearchBar from './ModelSearchBar';
import { extractInternals, FILTER_KEYWORDS, HIGHLIGHT_COLOR } from './modelViewerUtils';

function MusclesInteractive({ modelSrc }) {
  const navigate = useNavigate();
  const modelRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [currentSlice, setCurrentSlice] = useState(0);
  const [totalLayers, setTotalLayers] = useState(1);
  const [selectedHidden, setSelectedHidden] = useState(false);
  const [hiddenEntries, setHiddenEntries] = useState([]);
  const [showHiddenMenu, setShowHiddenMenu] = useState(false);

  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const allMeshesRef = useRef([]);
  const organizedRef = useRef(false);
  const originalMaterialsRef = useRef(new Map());
  const highlightedMeshRef = useRef(null);
  const originalRenderOrderRef = useRef(0);
  const raycasterRef = useRef(new THREE.Raycaster());
  const selectedMeshRef = useRef(null);
  const hiddenMeshesRef = useRef(new Set());
  const focusRef = useRef(null);

  const forceRender = useCallback(() => {
    const mv = modelRef.current;
    if (!mv) return;
    if (mv.requestUpdate) mv.requestUpdate();
    try {
      let proto = mv;
      for (let i = 0; i < 4 && proto; i++) {
        for (const s of Object.getOwnPropertySymbols(proto)) {
          if (s?.description === 'needsRender' && typeof mv[s] === 'function') {
            mv[s]();
          }
        }
        proto = Object.getPrototypeOf(proto);
      }
    } catch (e) { }
    try {
      const expAttr = mv.getAttribute('exposure') || '1';
      const exp = parseFloat(expAttr);
      mv.setAttribute('exposure', (exp + 0.000001).toString());
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (modelRef.current) modelRef.current.setAttribute('exposure', expAttr);
        });
      });
    } catch (e) { }
  }, []);

  const updateVisibility = useCallback((slice) => {
    allMeshesRef.current.forEach(mesh => {
      const si = mesh.userData.sliceIndex;
      let visible = si === undefined ? true : (si >= slice || mesh.userData.isWall === true);
      if (hiddenMeshesRef.current.has(mesh.uuid)) visible = false;
      mesh.visible = visible;
    });
    const mv = modelRef.current;
    if (mv && mv.requestUpdate) mv.requestUpdate();
    forceRender();
  }, [forceRender]);

  const organizeMeshes = useCallback(() => {
    if (organizedRef.current) return;
    const scene = sceneRef.current;
    if (!scene) return;

    const meshes = [];
    let minZ = Infinity, maxZ = -Infinity;

    scene.traverse((obj) => {
      if (obj.isMesh && obj.geometry) {
        obj.geometry.computeBoundingBox();
        const bbox = obj.geometry.boundingBox.clone();
        bbox.applyMatrix4(obj.matrixWorld);
        const center = bbox.getCenter(new THREE.Vector3());

        obj.userData.worldBBox = bbox;
        obj.userData.zCenter = center.z;
        minZ = Math.min(minZ, center.z);
        maxZ = Math.max(maxZ, center.z);

        meshes.push(obj);
      }
    });

    if (meshes.length === 0) return;

    const zRange = maxZ - minZ;
    if (zRange === 0) return;

    meshes.forEach(m => {
      const b = m.userData.worldBBox;
      m.userData.zThickness = b.max.z - b.min.z;
      m.userData.xyCx = (b.min.x + b.max.x) / 2;
      m.userData.xyCy = (b.min.y + b.max.y) / 2;
      m.userData.xyArea = Math.max((b.max.x - b.min.x) * (b.max.y - b.min.y), 1e-9);
    });

    const stackGap = zRange * 0.04;
    const areStacked = (a, b) => {
      if (a === b) return false;
      const ab = a.userData.worldBBox, bb = b.userData.worldBBox;
      const ox = Math.min(ab.max.x, bb.max.x) - Math.max(ab.min.x, bb.min.x);
      const oy = Math.min(ab.max.y, bb.max.y) - Math.max(ab.min.y, bb.min.y);
      if (ox <= 0 || oy <= 0) return false;
      const smaller = Math.min(a.userData.xyArea, b.userData.xyArea);
      if (ox * oy < 0.3 * smaller) return false;
      const aInB = a.userData.xyCx >= bb.min.x && a.userData.xyCx <= bb.max.x &&
        a.userData.xyCy >= bb.min.y && a.userData.xyCy <= bb.max.y;
      const bInA = b.userData.xyCx >= ab.min.x && b.userData.xyCx <= ab.max.x &&
        b.userData.xyCy >= ab.min.y && b.userData.xyCy <= ab.max.y;
      if (!aInB && !bInA) return false;
      const zSep = Math.max(ab.min.z, bb.min.z) - Math.min(ab.max.z, bb.max.z);
      const tol = Math.max(0.5 * Math.min(a.userData.zThickness, b.userData.zThickness), stackGap);
      return zSep <= tol;
    };

    meshes.forEach(m => {
      const mMax = m.userData.worldBBox.max.z;
      m.userData.isWall = !meshes.some(o => areStacked(m, o) && o.userData.worldBBox.max.z < mMax);
    });

    const frontFirst = [...meshes].sort((a, b) => b.userData.worldBBox.max.z - a.userData.worldBBox.max.z);
    let maxLayer = 0;
    frontFirst.forEach(m => {
      const mMax = m.userData.worldBBox.max.z;
      let best = -1;
      for (const f of meshes) {
        if (areStacked(m, f) && f.userData.worldBBox.max.z > mMax) {
          best = Math.max(best, f.userData.sliceIndex);
        }
      }
      m.userData.sliceIndex = best + 1;
      if (m.userData.sliceIndex > maxLayer) maxLayer = m.userData.sliceIndex;
    });
    setTotalLayers(maxLayer + 1);

    allMeshesRef.current = meshes;
    organizedRef.current = true;
    setTimeout(() => {
      updateVisibility(0);
      if (focusRef.current) focusRef.current();
    }, 100);
  }, [updateVisibility]);

  const highlightMesh = useCallback((mesh) => {
    if (!mesh || !mesh.material) return;

    if (!originalMaterialsRef.current.has(mesh.uuid)) {
      const orig = mesh.material;
      let cloned;
      if (Array.isArray(orig)) {
        cloned = orig.map(m => (m && typeof m.clone === 'function') ? m.clone() : null).filter(Boolean);
      } else if (orig && typeof orig.clone === 'function') {
        cloned = orig.clone();
      }
      if (cloned) {
        mesh.material = cloned;
        const mats = Array.isArray(cloned) ? cloned : [cloned];
        const props = mats.map(m => ({
          color: (m.color && typeof m.color.clone === 'function') ? m.color.clone() : new THREE.Color(0xffffff),
          opacity: m.opacity ?? 1,
          transparent: !!m.transparent,
          depthWrite: m.depthWrite ?? true,
          emissive: (m.emissive && typeof m.emissive.clone === 'function') ? m.emissive.clone() : new THREE.Color(0x000000),
          emissiveIntensity: m.emissiveIntensity ?? 1,
        }));
        originalMaterialsRef.current.set(mesh.uuid, props);
      }
    }

    originalRenderOrderRef.current = mesh.renderOrder;

    const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    mats.forEach(m => {
      if (!m) return;
      if (m.color) m.color.set(HIGHLIGHT_COLOR);
      m.opacity = 0.75;
      m.transparent = true;
      m.depthWrite = false;
      if (m.emissive) {
        m.emissive.set(0x000000);
        m.emissiveIntensity = 0;
      }
    });
    mesh.renderOrder = 999;

    highlightedMeshRef.current = mesh;
    forceRender();
  }, [forceRender]);

  const resetHighlight = useCallback(() => {
    const mesh = highlightedMeshRef.current;
    if (!mesh || !mesh.isMesh) return;
    const props = originalMaterialsRef.current.get(mesh.uuid);
    if (props) {
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      mats.forEach((m, i) => {
        const p = props[i];
        if (m && p) {
          if (m.color && p.color) m.color.set(p.color);
          m.opacity = p.opacity;
          m.transparent = p.transparent;
          m.depthWrite = p.depthWrite;
          if (m.emissive && p.emissive) m.emissive.set(p.emissive);
          if (p.emissiveIntensity !== undefined) m.emissiveIntensity = p.emissiveIntensity;
        }
      });
    }
    mesh.renderOrder = originalRenderOrderRef.current;
    highlightedMeshRef.current = null;
    forceRender();
  }, [forceRender]);

  const { selectedPart, hotspotPosition, screenSide, hotspotKey, applySelection, closeLabel, setScreenSide } =
    useMeshSelection({ infoLookup: getMuscleInfo, highlightMesh, resetHighlight });

  const handleModelClick = useCallback((event) => {
    const mv = modelRef.current;
    if (!mv) return;

    const rect = mv.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    const hit = mv.positionAndNormalFromPoint(x, y);
    if (!hit) {
      selectedMeshRef.current = null;
      closeLabel();
      return;
    }

    let identifiedMesh = null;
    let identifiedName = null;

    const scene = sceneRef.current;
    const camera = cameraRef.current;
    if (scene && camera) {
      const ndcX = (x / rect.width) * 2 - 1;
      const ndcY = -(y / rect.height) * 2 + 1;
      raycasterRef.current.setFromCamera(new THREE.Vector2(ndcX, ndcY), camera);
      const intersects = raycasterRef.current.intersectObjects(scene.children, true);
      const hitResult = intersects.find(i => {
        const obj = i.object;
        if (!obj.isMesh || !obj.visible) return false;
        const name = (obj.name || '').toLowerCase();
        const matName = (obj.material?.name || '').toLowerCase();
        return !FILTER_KEYWORDS.some(f => name.includes(f) || matName.includes(f));
      });
      if (hitResult) {
        identifiedMesh = hitResult.object;
        identifiedName = hitResult.object.name || hitResult.object.material?.name || null;
      }
    }

    if (!identifiedName) {
      const mvMat = mv.materialFromPoint(x, y);
      if (mvMat && mvMat.name) identifiedName = mvMat.name;
    }

    if (identifiedName && identifiedName !== 'Unknown') {
      selectedMeshRef.current = identifiedMesh;
      setSelectedHidden(identifiedMesh ? hiddenMeshesRef.current.has(identifiedMesh.uuid) : false);
      applySelection(identifiedMesh, hit.position, hit.normal);
    } else {
      selectedMeshRef.current = null;
      closeLabel();
    }
  }, [applySelection, closeLabel]);

  const closeLabelInner = useCallback(() => {
    closeLabel();
    selectedMeshRef.current = null;
  }, [closeLabel]);

  const toggleHideSelected = useCallback(() => {
    const mesh = selectedMeshRef.current;
    if (!mesh) return;
    const hidden = hiddenMeshesRef.current;
    if (hidden.has(mesh.uuid)) {
      hidden.delete(mesh.uuid);
      const si = mesh.userData.sliceIndex;
      mesh.visible = si === undefined ? true : (si >= currentSlice || mesh.userData.isWall === true);
      setSelectedHidden(false);
      setHiddenEntries(prev => prev.filter(e => e.uuid !== mesh.uuid));
    } else {
      hidden.add(mesh.uuid);
      mesh.visible = false;
      setSelectedHidden(true);
      const info = getMuscleInfo(mesh.name);
      setHiddenEntries(prev =>
        prev.some(e => e.uuid === mesh.uuid)
          ? prev
          : [...prev, { uuid: mesh.uuid, mesh, english: info.english || mesh.name }]
      );
    }
    const mv = modelRef.current;
    if (mv && mv.requestUpdate) mv.requestUpdate();
    forceRender();
  }, [currentSlice, forceRender]);

  const restoreVisibility = useCallback((mesh) => {
    const si = mesh.userData.sliceIndex;
    mesh.visible = si === undefined ? true : (si >= currentSlice || mesh.userData.isWall === true);
  }, [currentSlice]);

  const showHiddenMesh = useCallback((uuid) => {
    setHiddenEntries(prev => {
      const entry = prev.find(e => e.uuid === uuid);
      if (entry) {
        hiddenMeshesRef.current.delete(uuid);
        restoreVisibility(entry.mesh);
      }
      return prev.filter(e => e.uuid !== uuid);
    });
    if (selectedMeshRef.current && selectedMeshRef.current.uuid === uuid) {
      setSelectedHidden(false);
    }
    const mv = modelRef.current;
    if (mv && mv.requestUpdate) mv.requestUpdate();
    forceRender();
  }, [restoreVisibility, forceRender]);

  const showAllHidden = useCallback(() => {
    setHiddenEntries(prev => {
      prev.forEach(e => {
        hiddenMeshesRef.current.delete(e.uuid);
        restoreVisibility(e.mesh);
      });
      return [];
    });
    setShowHiddenMenu(false);
    setSelectedHidden(false);
    const mv = modelRef.current;
    if (mv && mv.requestUpdate) mv.requestUpdate();
    forceRender();
  }, [restoreVisibility, forceRender]);

  const clearSelection = useCallback(() => {
    closeLabel();
    selectedMeshRef.current = null;
  }, [closeLabel]);

  const handleSliderChange = useCallback((e) => {
    const val = parseInt(e.target.value, 10);
    setCurrentSlice(val);
    updateVisibility(val);
    clearSelection();
  }, [updateVisibility, clearSelection]);

  const handlePeel = useCallback(() => {
    if (currentSlice < totalLayers) {
      const next = currentSlice + 1;
      setCurrentSlice(next);
      updateVisibility(next);
      clearSelection();
    }
  }, [currentSlice, totalLayers, updateVisibility, clearSelection]);

  const handleRestore = useCallback(() => {
    if (currentSlice > 0) {
      const next = currentSlice - 1;
      setCurrentSlice(next);
      updateVisibility(next);
      clearSelection();
    }
  }, [currentSlice, updateVisibility, clearSelection]);

  const handleShowAll = useCallback(() => {
    setCurrentSlice(0);
    hiddenMeshesRef.current.clear();
    setHiddenEntries([]);
    setShowHiddenMenu(false);
    updateVisibility(0);
    clearSelection();
  }, [updateVisibility, clearSelection]);

  const onReveal = useCallback(async (mesh, entry) => {
    if (entry && entry.reveal === 'peel') {
      const si = mesh.userData.sliceIndex;
      if (typeof si === 'number' && si > currentSlice) {
        setCurrentSlice(si);
        updateVisibility(si);
        await new Promise((res) => setTimeout(res, 160));
      }
    }
  }, [currentSlice, updateVisibility]);

  const onFound = useCallback((mesh, center, normal) => {
    selectedMeshRef.current = mesh;
    setSelectedHidden(mesh ? hiddenMeshesRef.current.has(mesh.uuid) : false);
    applySelection(mesh, center, normal);
  }, [applySelection]);

  const { focusMeshIfPending, focusMesh } = useFocusMeshOnLoad({ modelRef, onFound, onReveal });

  useEffect(() => { focusRef.current = focusMeshIfPending; }, [focusMeshIfPending]);

  useEffect(() => {
    const mv = modelRef.current;
    if (!mv) return;

    const handleLoad = () => {
      setIsLoading(false);
      setLoadError(false);
      sceneRef.current = null;
      cameraRef.current = null;
      const { scene, camera } = extractInternals(mv);
      if (scene) sceneRef.current = scene;
      if (camera) cameraRef.current = camera;
      setTimeout(() => organizeMeshes(), 500);
    };

    const handleProgress = (e) => {
      if (e.detail?.totalProgress === 1) {
        setIsLoading(false);
        const { scene, camera } = extractInternals(mv);
        if (scene) sceneRef.current = scene;
        if (camera) cameraRef.current = camera;
        setTimeout(() => organizeMeshes(), 500);
      }
    };

    const handleError = () => {
      setIsLoading(false);
      setLoadError(true);
    };

    mv.addEventListener('load', handleLoad);
    mv.addEventListener('progress', handleProgress);
    mv.addEventListener('error', handleError);
    if (mv.loaded) handleLoad();

    const timeout = setTimeout(() => setIsLoading(false), 15000);

    return () => {
      mv.removeEventListener('load', handleLoad);
      mv.removeEventListener('progress', handleProgress);
      mv.removeEventListener('error', handleError);
      clearTimeout(timeout);
    };
  }, [organizeMeshes, focusMeshIfPending]);

  useEffect(() => {
    if (!selectedPart || !hotspotPosition) return;

    const mv = modelRef.current;
    if (!mv) return;

    let isRunning = false;
    const worldPos = new THREE.Vector3();
    const posParts = hotspotPosition.position.replace(/m/g, '').split(' ').map(parseFloat);
    worldPos.set(posParts[0], posParts[1], posParts[2]);

    const checkSide = () => {
      if (isRunning) return;
      isRunning = true;
      requestAnimationFrame(() => {
        try {
          const camera = cameraRef.current;
          if (camera && mv) {
            const ndc = worldPos.clone().project(camera);
            const rect = mv.getBoundingClientRect();
            const screenX = (ndc.x + 1) / 2 * rect.width;
            const sideNow = screenX < (rect.width / 2) ? 'left' : 'right';
            setScreenSide(prev => (prev !== sideNow ? sideNow : prev));
          }
        } catch (e) { }
        isRunning = false;
      });
    };

    mv.addEventListener('camera-change', checkSide);
    return () => mv.removeEventListener('camera-change', checkSide);
  }, [selectedPart, hotspotPosition]);

  return (
    <div className="model-viewer-page muscles-page">
      <button onClick={() => navigate('/menu')} className="back-btn">
        ← Back to Menu
      </button>

      <ModelSearchBar currentSystemId="muscles" onFocusMesh={focusMesh} />

      {isLoading && (
        <div className="skeleton-mv-loading">
          <div className="skeleton-loading-spinner"></div>
          <p>Loading 3D Muscular System...</p>
        </div>
      )}

      {loadError && (
        <div className="skeleton-mv-loading">
          <p>Unable to load 3D model</p>
        </div>
      )}

      {!isLoading && (
        <div className="muscles-layer-strip">
          <button
            className="muscles-layer-btn muscles-layer-plus"
            onClick={handleRestore}
            disabled={currentSlice <= 0}
            title="Restore layer"
          >
            +
          </button>

          <div className="muscles-slider-container">
            <input
              type="range"
              className="muscles-slider"
              min="0"
              max={totalLayers}
              value={currentSlice}
              onChange={handleSliderChange}
              orient="vertical"
            />
          </div>

          <button
            className="muscles-layer-btn muscles-layer-minus"
            onClick={handlePeel}
            disabled={currentSlice >= totalLayers}
            title="Peel layer"
          >
            −
          </button>

          <button
            className="muscles-reset-btn"
            onClick={handleShowAll}
            disabled={currentSlice === 0}
          >
            Reset
          </button>
        </div>
      )}

      {hiddenEntries.length > 0 && (
        <div className="muscles-hidden-panel">
          <button
            className="muscles-hidden-toggle"
            onClick={() => setShowHiddenMenu(v => !v)}
            title="Hidden muscles"
            aria-label="Hidden muscles"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
              <line x1="1" y1="1" x2="23" y2="23" />
            </svg>
            <span className="muscles-hidden-badge">{hiddenEntries.length}</span>
          </button>

          {showHiddenMenu && (
            <>
              <div className="muscles-hidden-backdrop" onClick={() => setShowHiddenMenu(false)} />
              <div className="muscles-hidden-dropdown">
                <div className="muscles-hidden-header">
                  <span>Hidden muscles</span>
                  <button className="muscles-hidden-showall" onClick={showAllHidden}>Show all</button>
                </div>
                <div className="muscles-hidden-list">
                  {hiddenEntries.map(e => (
                    <div className="muscles-hidden-item" key={e.uuid}>
                      <span className="muscles-hidden-name">{e.english}</span>
                      <button className="muscles-hidden-show" onClick={() => showHiddenMesh(e.uuid)}>Show</button>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      <div className="muscles-hint">
        Tap a muscle to identify it · Use the slider to dissect layers
      </div>

      <model-viewer
        ref={modelRef}
        id="muscles-viewer"
        src={modelSrc}
        alt="3D Muscular System"
        ar
        ar-modes="webxr scene-viewer quick-look"
        camera-controls
        touch-action="none"
        interaction-prompt="auto"
        loading="eager"
        reveal="auto"
        shadow-intensity="1"
        exposure="1"
        environment-image="neutral"
        camera-orbit="0deg 75deg 2.5m"
        min-camera-orbit="auto auto 0.5m"
        max-camera-orbit="auto auto 4m"
        field-of-view="45deg"
        minimum-render-scale="0.5"
        power-preference="low-power"
        quick-look-browsers="safari chrome"
        onClick={handleModelClick}
        style={{
          width: '100%',
          height: '100%',
          backgroundColor: '#ffffff',
          '--progress-bar-color': '#00E5FF',
          '--progress-bar-height': '4px',
          cursor: 'var(--cursor-pointer)',
        }}
      >
        {selectedPart && hotspotPosition && (
          <div
            key={hotspotKey}
            className={`skeleton-leader-hotspot ${screenSide}`}
            slot="hotspot-muscle"
            data-position={hotspotPosition.position}
            data-normal={hotspotPosition.normal}
          >
            <div className="skeleton-leader-dot"></div>
            <div className="skeleton-leader-line"></div>
            <div className="skeleton-leader-label">
              <div className="skeleton-leader-top">
                <div className="skeleton-leader-content">
                  <span className="skeleton-leader-english">{selectedPart.english}</span>
                  <span className="skeleton-leader-latin">{selectedPart.latin}</span>
                </div>
                <button className="skeleton-leader-close" onClick={closeLabelInner}>✕</button>
              </div>
              <button className="skeleton-leader-toggle" onClick={toggleHideSelected}>
                {selectedHidden ? 'Show' : 'Hide'}
              </button>
            </div>
          </div>
        )}
      </model-viewer>
    </div>
  );
}

function Muscles() {
  if (isIOS) {
    return <MusclesInteractive modelSrc="/models/MuscelsSys.ios.glb" />;
  }
  return <MusclesInteractive modelSrc="/models/MuscelsSys.glb" />;
}

export default Muscles;
